import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { useTheme } from "@hooks/useTheme";
import { CVE_LINK } from "@constants/index";
import { formatDate, formatDateTime } from "@utils/common";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import {
  VULN_STATUS_I18N_KEYS,
  VULN_STATUS_OPTIONS,
  translateVulnStatus,
} from "./cveVulnStatus";

export default function CveAnalysisList() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [cveList, setCveList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(
    ITEMS_PER_PAGE_KEY.CVE_ANALYSIS,
    DEFAULT_ITEMS_PER_PAGE,
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const isInitialMount = useRef(true);
  const prevCurrentPage = useRef(1);
  const [debouncedSearchFilters, setDebouncedSearchFilters] = useState(searchFilters);
  const debounceTimerRef = useRef(null);
  const [isSearching, setIsSearching] = useState(false);

  const getListData = useMutationData(
    queryKeys.getCveAnalysisList,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setCveList(response.object || []);
          setTotalData(response.objectCount || 0);
          setTotalPages(Math.ceil((response.objectCount || 0) / itemsPerPage));
        }
      },
      onError: (error) => {
        showErrorToast(t("preferences.loading-data-error"));
        console.error("Error during get CVE analysis data:", error);
      },
    },
  );

  const searchFields = [
    {
      name: "deletedFlag",
      label: t("cve-browsing.status"),
      type: "select",
      placeholder: t("cve-browsing.select-status"),
      options: [
        { value: "N", label: t("cve-browsing.status-active") },
        { value: "Y", label: t("cve-browsing.status-masked") },
      ],
    },
    {
      name: "vulnStatus",
      label: t("cve-browsing.vuln-status"),
      type: "select",
      placeholder: t("cve-browsing.select-vuln-status"),
      options: VULN_STATUS_OPTIONS.map((status) => ({
        value: status,
        label: t(VULN_STATUS_I18N_KEYS[status]),
      })),
    },
  ];

  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields,
    quickSearchField: "keyword",
    setSearchCriteria: setSearchFilters,
  });

  const fetchData = useCallback(() => {
    if (getListData.isPending) {
      return;
    }

    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "keyword",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      const value = effectiveCriteria[key];
      if (value !== "" && value !== undefined && value !== null) {
        if (key === "createdAtFrom" || key === "createdAtTo") {
          acc[key] = formatDate(value);
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    bodyRequest.pageSize = itemsPerPage;
    bodyRequest.startRow = itemsPerPage * (currentPage - 1);

    getListData.mutate(bodyRequest);
  }, [searchFilters, currentPage, itemsPerPage, getListData.isPending, showAdvancedSearch]);

  useEffect(() => {
    if (!isInitialMount.current) {
      fetchData();
    }
  }, [showAdvancedSearch]);

  useEffect(() => {
    if (searchFilters && typeof searchFilters === "object") {
      fetchData();
      isInitialMount.current = false;
    }
  }, [debouncedSearchFilters, itemsPerPage]);

  useEffect(() => {
    if (!isInitialMount.current && prevCurrentPage.current !== currentPage) {
      fetchData();
      prevCurrentPage.current = currentPage;
    }
  }, [currentPage]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const hasActualChange = Object.values(searchFilters).some((value) => value && value !== "");
    if (hasActualChange) {
      setIsSearching(true);
    }
    if (!isInitialMount.current) {
      debounceTimerRef.current = setTimeout(() => {
        setDebouncedSearchFilters(searchFilters);
        setIsSearching(false);
      }, 500);
    } else {
      setDebouncedSearchFilters(searchFilters);
      setIsSearching(false);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchFilters]);

  const columns = [
    "cveId",
    "deletedFlag",
    "vulnStatus",
    "syncedAt",
    "createdAt",
  ];

  const columnConfig = {
    cveId: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
        width: "22%",
      },
      headerRender: () => t("cve-browsing.cve-id"),
      customRender: (value) => (
        <a
          className={`text-base font-semibold ${colors.textActive}`}
          href={`${CVE_LINK}/${value}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(`${CVE_LINK}/${value}`, "_blank", "noopener,noreferrer");
          }}
        >
          {value || "-"}
        </a>
      ),
    },
    deletedFlag: {
      style: {
        textAlign: "center",
        width: "14%",
      },
      headerRender: () => t("cve-browsing.status"),
      customRender: (value) =>
        value === "Y" ? t("cve-browsing.status-masked") : t("cve-browsing.status-active"),
    },
    vulnStatus: {
      style: {
        textAlign: "center",
        width: "18%",
      },
      headerRender: () => t("cve-browsing.vuln-status"),
      customRender: (value) => translateVulnStatus(t, value),
    },
    syncedAt: {
      headerRender: () => t("cve-browsing.synced-at"),
      customRender: (value) => (value ? formatDateTime(value) : "-"),
    },
    createdAt: {
      headerRender: () => t("cve-browsing.created-at"),
      customRender: (value) => (value ? formatDateTime(value) : "-"),
    },
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1);
  };

  const searchHandler = useCallback((inputData) => {
    setSearchFilters((prev) => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const onRowClick = (row) => {
    if (row?.cveVersionAnalysisSeq == null) {
      return;
    }
    navigate(`./${row.cveVersionAnalysisSeq}`);
  };

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="cve-browsing-page">
      <div className={`${colors.textPlaceholder} py-1`}>
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`}>
            {t("cve-browsing.title")}
          </h1>
        </div>
      </div>

      <div className={`flex-shrink-0 pt-4 pr-4 ${colors.bgSecondary}`}>
        <TableHeaderControls
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          searchField="keyword"
          searchValue={searchFilters.keyword ?? ""}
          placeholderSearch={t("cve-browsing.search-placeholder")}
          isDisplayAddButton={false}
          isDisplayDeleteButton={false}
          isSearching={isSearching}
          searchInputTestId="cve-browsing-search-input"
        />
      </div>

      {showAdvancedSearch && (
        <div data-testid="cve-browsing-advanced-search">
          <AdvancedSearch
            title={t("common.advanced-search")}
            searchFields={[...searchFields]}
            searchHandler={searchHandler}
            withoutSearch={true}
            data-testid="cve-browsing-advanced-search-form"
          />
        </div>
      )}

      <div className="flex-grow min-h-0" data-testid="cve-browsing-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={cveList}
          columns={columns}
          loading={getListData.isPending}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          selectedRows={selectedRows}
          disabledRow={[]}
          onRowClick={onRowClick}
          shouldShowGoToPage={true}
          showItemsPerPageSelector={true}
        />
      </div>
    </div>
  );
}
